// Spades on the table in 3D: the played cards on the felt (the last trick
// stays a moment), seat labels with bids / tricks, and the scoreboard over
// the table. Table-local, floor origin (see GameTable3D).

import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { Billboard, Text } from '@react-three/drei';
import { TABLE_TOP, seatXZ, seatYaw } from '../tableLayout';
import { cardTexture } from '../cardFaces';
import { SEAT_NAMES, type SpadesState } from '../../../../supabase/functions/games/spades.ts';
import { vrColors as colors, noRaycast } from '../../VR/vrUi';
import type { Table3DProps } from '../types';

export function CardMesh({ card, position, rotation, size = 0.11 }: { card: string; position: [number, number, number]; rotation?: [number, number, number]; size?: number }) {
  const tex = useMemo(() => cardTexture(card), [card]);
  return (
    <mesh position={position} rotation={rotation ?? [-Math.PI / 2, 0, 0]} raycast={noRaycast as any}>
      <planeGeometry args={[size, size * 1.4]} />
      <meshStandardMaterial map={tex} roughness={0.6} side={THREE.DoubleSide} />
    </mesh>
  );
}

const PRESENT_TRICK_MS = 2200;

export function SpadesTable3D({ def, table, seat }: Table3DProps) {
  const st = table?.state;
  const g = st?.g as SpadesState | null | undefined;

  // Last completed trick stays on the felt for a moment.
  const [showLast, setShowLast] = useState(false);
  const lastKey = g?.lastTrick ? `${g.handNo}:${g.played.length}` : '';
  useEffect(() => {
    if (!lastKey) return;
    setShowLast(true);
    const t = window.setTimeout(() => setShowLast(false), PRESENT_TRICK_MS);
    return () => window.clearTimeout(t);
  }, [lastKey]);
  const plays = g ? (g.trick.length ? g.trick : showLast && g.lastTrick ? g.lastTrick.plays : []) : [];

  return (
    <>
      {/* Cards on the felt, each in front of the seat that played it. */}
      {plays.map((p, k) => {
        const [x, z] = seatXZ(p.seat, 0.3);
        return <CardMesh key={`${p.seat}-${p.card}`} card={p.card} position={[x, TABLE_TOP + 0.003 + k * 0.001, z]}
          rotation={[-Math.PI / 2, 0, -seatYaw(p.seat) + Math.PI]} />;
      })}
      {/* Seat labels */}
      {[0, 1, 2, 3].map((i) => {
        const s = st?.seats[i];
        const [x, z] = seatXZ(i, 1.15);
        const bid = g?.bids[i];
        const line2 = g && st?.phase !== 'lobby'
          ? `${bid === null || bid === undefined ? 'bidding…' : bid === 0 ? 'Nil' : `Bid ${bid}`} · ${g.tricksWon[i]} won`
          : '';
        const turn = g && (g.phase === 'bidding' || g.phase === 'playing') && g.turn === i && st?.phase === 'playing';
        return (
          <Billboard key={i} position={[x, 1.45, z]}>
            <Text fontSize={0.085} color={turn ? colors.teal : '#ffffff'} outlineWidth={0.006} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
              {s ? `${s.name}${s.kind === 'bot' ? ' 🤖' : ''}${i === seat ? ' (you)' : ''}` : `${SEAT_NAMES[i]} — open`}
            </Text>
            {line2 && <Text position={[0, -0.02, 0]} fontSize={0.065} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">{line2}</Text>}
          </Billboard>
        );
      })}
      {/* Scoreboard over the table */}
      <Billboard position={[0, 1.75, 0]}>
        <Text fontSize={0.11} color="#ffffff" outlineWidth={0.007} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
          {def.label}
        </Text>
        <Text position={[0, -0.02, 0]} fontSize={0.075} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">
          {!st ? 'Loading…'
            : st.phase === 'lobby' ? `${st.seats.filter(Boolean).length}/4 seated — walk up to play`
            : g ? `N/S ${g.scores[0]} (${g.bags[0]} bags)  ·  E/W ${g.scores[1]} (${g.bags[1]} bags)` : ''}
        </Text>
        {st?.phase === 'over' && g && (
          <Text position={[0, -0.13, 0]} fontSize={0.08} color={colors.teal} outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">
            {`${g.winner === 0 ? 'North / South' : 'East / West'} win!`}
          </Text>
        )}
      </Billboard>
    </>
  );
}

export default SpadesTable3D;
